/**
 * OCSEF's pre-approval flowchart, and the forms it points to.
 *
 * Source: OCSEF, "Do I Need SRC Pre-Approval? What Forms Do I Need?" (revised
 * for 2027). The branch questions and their examples below are transcribed from
 * that chart; the forms follow from it in formsFor(). OCSEF's own caveat holds:
 * it is not a complete list of examples.
 *
 * The wizard in Step 6 answers these branches from the student's writing; the
 * Requirements page tracks what the student confirmed. Form PDFs follow the S3
 * media convention (assets.ardentacademy.com/research/forms/2026-27/…).
 */

const PDF = (name: string) =>
  `https://assets.ardentacademy.com/research/forms/2026-27/${name}.pdf`;

export const FLOWCHART_URL = PDF("ocsef-pre-approval-flowchart-2027");

export const OCSEF_SOURCE =
  "From OCSEF's flowchart “Do I Need SRC Pre-Approval? What Forms Do I Need?” (revised for 2027). It is not a complete list of examples — check www.ocsef.org for the complete rules, or email info@ocsef.org.";

export const OCSEF_IF_IN_DOUBT =
  "OCSEF's advice: if in doubt, complete the form(s) and submit for pre-approval. Projects registered after the pre-approval deadline that turn out to need it will not be approved.";

export type BranchKey = "people" | "animals" | "bio" | "tissue" | "hazards";
export type Verdict = "yes" | "no" | "unsure";
export type AnimalsWhere = "school_home_field" | "rri" | "unknown";

export interface Answers {
  people?: boolean;
  animals?: boolean;
  animals_where?: AnimalsWhere;
  bio?: boolean;
  tissue?: boolean;
  hazards?: boolean;
  /** Not on OCSEF's chart: an ISEF form the student answers directly. */
  continuation?: boolean;
}

export const BRANCHES: {
  key: BranchKey;
  question: string;
  examples: string[];
}[] = [
  {
    key: "people",
    question: "Does your project involve human participants?",
    examples: [
      "Survey/questionnaire (online or in-person)",
      "Completing a task/activity or being tested",
      "Testing or using your device by others",
      "Observing and interacting with humans",
      "Human dataset with identifiable information",
    ],
  },
  {
    key: "animals",
    question: "Does your project involve vertebrate animals?",
    examples: [
      "Mammals, fish, birds, reptiles, amphibians, or cephalopods",
      "Testing or observation of animals in nature or a controlled setting",
    ],
  },
  {
    key: "bio",
    question: "Does your project involve biological hazards?",
    examples: [
      "Bacteria, viruses, yeast, mold, fungi, viroids, rickettsia, cyanobacteria, or parasites",
      "Tissues, cell cultures, cell lines",
      "Samples from a human or animal body (teeth, hair, bone, saliva, blood, swabs)",
      "Samples from the environment (water, soil)",
      "DNA, RNA, or recombinant DNA",
    ],
  },
  {
    key: "tissue",
    question: "Does your project involve human or animal tissue?",
    examples: [
      "Fresh/frozen tissue sample",
      "Fresh organ or other body part",
      "Blood or body fluids",
      "Primary cell/tissue cultures",
      "Vertebrate established cell lines",
    ],
  },
  {
    key: "hazards",
    question:
      "Does your project involve potentially hazardous chemicals, materials, devices, or activities?",
    examples: [
      "Household cleaning agents, solvents, acids, bases, chemicals, ferrofluid, gases, or biofuel",
      "3D printers, lasers, UV light, radiation, magnets, charcoal, rockets, flammable material, or testing pH",
      "Oven/stove, saw/drill, soldering, generator, circuits, motor, electricity, or batteries",
      "Prescription drugs, tobacco, explosives, or pyrophoric chemicals",
      "Review the Safety Data Sheets",
    ],
  },
];

export const ANIMALS_WHERE_QUESTION = "Where will the animal work happen?";
export const ANIMALS_WHERE_OPTIONS: { value: AnimalsWhere; label: string }[] = [
  { value: "school_home_field", label: "School, home, or out in nature" },
  {
    value: "rri",
    label: "A university, hospital, or company lab (regulated research)",
  },
];

export const CONTINUATION_QUESTION =
  "Is this project a continuation of your project from a previous year?";

/** OCSEF's chart, in one place: answers in, form keys out. */
export function formsFor(a: Answers): string[] {
  const out = new Set<string>();
  if (a.people) {
    out.add("3");
    out.add("4");
    out.add("OCSEF_CONSENT");
    out.add("OCSEF_PLAN");
  }
  if (a.animals) out.add(a.animals_where === "rri" ? "5B" : "5A");
  if (a.bio) out.add("6A");
  if (a.tissue) {
    out.add("6A");
    out.add("6B");
  }
  if (a.hazards) out.add("3");
  if (a.animals_where === "rri") out.add("2C");
  if (a.bio || a.tissue || a.animals_where === "rri") out.add("2B");
  if (a.continuation) out.add("7");
  return Array.from(out);
}

/** True when OCSEF's chart says the project needs SRC pre-approval. */
export const needsPreApproval = (a: Answers): boolean =>
  !!(a.people || a.animals || a.bio || a.tissue || a.hazards);

export type FormStatus = "not_started" | "filled" | "signed" | "submitted";

export const FORM_STATUS_LABEL: Record<FormStatus, string> = {
  not_started: "Not started",
  filled: "Filled out",
  signed: "Signed",
  submitted: "Submitted",
};

export interface FairForm {
  key: string;
  code: string;
  title: string;
  group: string;
  /** Marked as suggested for these answers. */
  suggested: (a: Answers) => boolean;
  purpose: string;
  signs: string;
  when: string;
  tips: string[];
  /** Who the student asks for a signature; drives "Draft a request". */
  ask?: string;
  url: string;
}

export const FORM_GROUPS = [
  "Every project",
  "If people take part",
  "Vertebrate animals",
  "Biological agents and tissue",
  "Outside expertise or labs",
  "Continuing last year's project",
];

export const FAIR_FORMS: FairForm[] = [
  {
    key: "1B",
    code: "Form 1B",
    title: "Approval Form",
    group: "Every project",
    suggested: () => true,
    purpose:
      "You confirm you'll follow the fair's rules and ethics; your parent consents to the research.",
    signs: "You and your parent. The fair's committee signs later.",
    when: "Before you start your experiment.",
    tips: [
      "Read the ethics statement before signing.",
      "The date must be before your experiment begins.",
    ],
    ask: "your parent — show them your research plan and explain any risks",
    url: PDF("form-1b-approval-form"),
  },
  {
    key: "3",
    code: "Form 3",
    title: "Risk Assessment",
    group: "Every project",
    suggested: (a) => !!a.people || !!a.hazards,
    purpose:
      "List any hazards, how you'll stay safe, how you'll dispose of materials, and where your safety information came from.",
    signs: "Your direct supervisor (the adult who watches your experiment).",
    when: "Before your experiment. OCSEF requires it for projects with human participants, or with hazardous chemicals, materials, devices, or activities — and recommends it for every project.",
    tips: [
      "Answer every question.",
      "\"No risk\" isn't an acceptable answer — name small risks and how you'll handle them.",
      "List real safety sources, such as the Safety Data Sheet for anything you use.",
    ],
    ask: "your direct supervisor — send the form with your research plan",
    url: PDF("form-3-risk-assessment"),
  },
  {
    key: "OCSEF_PLAN",
    code: "OCSEF",
    title: "Human Participant Research Plan",
    group: "If people take part",
    suggested: (a) => !!a.people,
    purpose:
      "Describe who takes part, what they'll do, what data you collect, the risks, and how you protect privacy.",
    signs: "No signature — you give it to your school's review staff.",
    when: "Before your experiment, together with your consent form.",
    tips: [
      "Include your survey or test questions.",
      "Explain how names and data stay private.",
    ],
    url: PDF("ocsef-human-participant-research-plan"),
  },
  {
    key: "OCSEF_CONSENT",
    code: "OCSEF",
    title: "Informed Consent Template",
    group: "If people take part",
    suggested: (a) => !!a.people,
    purpose:
      "Tells participants (or their parents) about your study, and collects their permission.",
    signs: "Each participant; a parent for anyone under 18.",
    when: "Approved by the fair committee before you hand it out.",
    tips: [
      "Write it with your sponsor.",
      "Keep signed forms private, and shred them after the fair.",
    ],
    ask: "your sponsor — to help you write it before the fair committee reviews it",
    url: PDF("ocsef-informed-consent-template"),
  },
  {
    key: "4",
    code: "Form 4",
    title: "Human Participants",
    group: "If people take part",
    suggested: (a) => !!a.people,
    purpose: "Your school's review board approves your study.",
    signs:
      "Three school staff: the principal or vice principal, a teacher who is not your sponsor, and the school nurse or counselor. None can be your sponsor or a relative.",
    when: "Before you recruit anyone.",
    tips: ["Attach your research plan, surveys, and consent form."],
    ask: "three school staff — the principal or vice principal, a teacher who is not your sponsor, and the school nurse or counselor (one short request each)",
    url: PDF("form-4-human-participants"),
  },
  {
    key: "5A",
    code: "Form 5A",
    title: "Vertebrate Animal",
    group: "Vertebrate animals",
    suggested: (a) => !!a.animals && a.animals_where !== "rri",
    purpose:
      "The species and number, housing and care, and what happens to the animals afterward.",
    signs:
      "The fair committee, plus a supervisor or veterinarian as the committee requires.",
    when: "Committee approval before your experiment. This is the form for work at school, home, or in nature.",
    tips: [],
    ask: "your supervisor or a veterinarian, as the fair committee requires",
    url: PDF("form-5a-vertebrate-animal"),
  },
  {
    key: "5B",
    code: "Form 5B",
    title: "Vertebrate Animal at a Research Institution",
    group: "Vertebrate animals",
    suggested: (a) => !!a.animals && a.animals_where === "rri",
    purpose: "Your role at the lab.",
    signs: "The lab's scientist.",
    when: "After your experiment; the institution's animal approval must come first.",
    tips: [],
    ask: "the scientist at the lab",
    url: PDF("form-5b-vertebrate-animal-at-rri"),
  },
  {
    key: "6A",
    code: "Form 6A",
    title: "Biological Agents",
    group: "Biological agents and tissue",
    suggested: (a) => !!a.bio || !!a.tissue,
    purpose:
      "Each agent's source and safety level, the lab's safety level, precautions, disposal, and your training. OCSEF counts environmental samples (water, soil) and DNA or RNA here too.",
    signs: "A qualified scientist or supervisor.",
    when: "Before your experiment.",
    tips: [],
    ask: "a qualified scientist or supervisor",
    url: PDF("form-6a-potentially-hazardous-biological-agents"),
  },
  {
    key: "6B",
    code: "Form 6B",
    title: "Human & Vertebrate Animal Tissue",
    group: "Biological agents and tissue",
    suggested: (a) => !!a.tissue,
    purpose: "The tissue type and where it comes from. Filed with Form 6A.",
    signs: "A qualified scientist or supervisor.",
    when: "Before your experiment.",
    tips: [],
    ask: "a qualified scientist or supervisor",
    url: PDF("form-6b-human-vertebrate-animal-tissue"),
  },
  {
    key: "2B",
    code: "Form 2B",
    title: "Qualified Scientist",
    group: "Outside expertise or labs",
    suggested: (a) => !!a.bio || !!a.tissue || a.animals_where === "rri",
    purpose: "The scientist's background and how they'll supervise you.",
    signs: "The scientist.",
    when: "Before your experiment. Needed if your project needs a qualified scientist.",
    tips: [],
    ask: "the qualified scientist",
    url: PDF("form-2b-qualified-scientist"),
  },
  {
    key: "2C",
    code: "Form 2C",
    title: "Research Institution",
    group: "Outside expertise or labs",
    suggested: (a) => a.animals_where === "rri",
    purpose: "What you did at that lab. One form per lab.",
    signs: "Your supervisor at the lab.",
    when: "After your experiment.",
    tips: [],
    ask: "your supervisor at the lab",
    url: PDF("form-2c-regulated-research-institution"),
  },
  {
    key: "7",
    code: "Form 7",
    title: "Continuation",
    group: "Continuing last year's project",
    suggested: (a) => !!a.continuation,
    purpose: "What's new compared with last year.",
    signs: "You.",
    when: "Can be after your experiment. Attach last year's abstract and research plan.",
    tips: [],
    url: PDF("form-7-continuation"),
  },
];

export const FORM_BY_KEY: Record<string, FairForm> = Object.fromEntries(
  FAIR_FORMS.map((f) => [f.key, f]),
);
